import { useState, useEffect, useCallback } from 'react'
import { useNavigate, Link, useLocation, NavLink } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { ELEVATEOS_LOGO_ALT } from '@/constants'
import { Avatar } from '@/components/ui/avatar'

import {
  dashModalChildrenPathname,
  exploreChildrenPathname,
  getChatFeedFlagsDueToRole,
  getParentItemHighlighted,
  marketingChildrenPathname,
  operationsChildrenPathname,
  removeAuthCookie,
  reservationsModalChildrenPathname,
  userMenuModalChildrenPathname,
} from '../helpers/helpers'
import { useDesignVersionPath } from '@/hooks'
import { clearImpersonation } from '@/utils/auth'

import { getAssetLink } from '../helpers/get-asset-link'
import { selectUserData, selectUserRole, logoutUser } from '../reducers/userDataReducer'
import { NAVIGATION_LABELS } from '../helpers/constants'

import NavbarBuildingSelector from './NavbarBuildingSelector/NavbarBuildingSelector'
import Icon from './Icons'
import Can from './Can'
import UserMenuModal from './NavbarModals/UserMenu'
import MarketingDropdown from './NavbarModals/MarketingDropdown'
import OperationsModal from './NavbarModals/OperationsModal'
import DashModal from './NavbarModals/DashModal'
import ExploreModal from './NavbarModals/ExploreModal'
import NavigationLabel from './NavigationLabel/NavigationLabel'
import UnifiedInboxLink from './NavbarModals/UpsellingLinks/UnifiedInboxLink'
import { getLogoLink } from './helpers'

const Navbar = () => {
  const { t } = useTranslation(['navbar'])
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()

  const userData = useSelector(selectUserData)
  const userRole = useSelector(selectUserRole)

  const clientsPath = useDesignVersionPath('clients', {
    legacy: '/dashboard/clients/legacy',
    new: '/dashboard/clients',
  })

  const [menuActive, setMenuActive] = useState(false)
  const [burgerOpen, setBurgerOpen] = useState(false)

  const { firstName, lastName, avatar, role } = userData.user || {}

  // EL-13392: the Reservations page is the only one made responsive so far, and it is reached by
  // saved/direct link on tablet and mobile. Below 1216px this collapses the nav links behind a
  // burger (see responsive.scss) — scoped to this route so the other, still desktop-only pages
  // keep their navigation exactly as it is.
  // EL-13613: the redesigned Reservations page (bookings/time-blocks/amenities/detail) lives at
  // its own top-level /reservations prefix rather than under /dashboard/building-dashboard - see
  // reservationsRoutesConfig in router/routes/dashboard.routes.tsx. startsWith (not includes) so
  // this doesn't also match unrelated routes that merely contain "reservations", e.g.
  // /dashboard/client-reservations.
  const isReservationsRoute =
    location.pathname.includes('/building-dashboard/reservations') ||
    location.pathname.startsWith('/reservations')

  /** 🔹 Handle dropdown toggle */
  const onDropdownMenu = useCallback((e, modalType) => {
    e.preventDefault()
    if (
      e.target.closest('.navmgr-hover-menu__inner') ||
      e.target.closest('.navmgr-hover-menu-user__inner')
    )
      return
    setMenuActive((prev) => (prev === modalType ? false : modalType))
  }, [])

  /** 🔹 Handle Logout */
  const onLogout = useCallback((e) => {
    e.preventDefault()
    removeAuthCookie('token')
    removeAuthCookie('role')
    // A stale tab can still show Logout after another tab started impersonating.
    clearImpersonation()
    navigate('/login', { replace: true })
    dispatch(logoutUser())
  }, [])

  const handleOpenReservations = (e) => {
    onDropdownMenu(e, 'reservations')
  }

  /** 🔹 Handle Click Outside */
  const clickHandler = useCallback((e) => {
    const isOutside =
      !e.target.closest('.navmgr-selector-item') && !e.target.closest('.navmgr-item-user')
    const isNavItem =
      e.target.closest('.hover-menu-item__link') || e.target.closest('.hover-menu-small-item')
    if (isOutside || isNavItem) {
      setMenuActive(false)
    }
    // EL-13392: dismiss the burger panel when the click lands outside the navbar entirely.
    if (!e.target.closest('.navmgr')) {
      setBurgerOpen(false)
    }
  }, [])

  useEffect(() => {
    document.body.addEventListener('click', clickHandler)
    return () => {
      document.body.removeEventListener('click', clickHandler)
    }
  }, [clickHandler])

  useEffect(() => {
    setMenuActive(false)
    setBurgerOpen(false)
  }, [location])

  useEffect(() => {
    const allMenus = document.querySelectorAll('[class^="navmgr-hover-menu-"]')

    allMenus.forEach((el) => {
      el.style.display = 'none'
    })

    allMenus.forEach((el) => {
      if (menuActive === 'user') {
        if (el.classList.contains(`navmgr-hover-menu-user`)) {
          el.style.display = 'flex'
        }
        if (el.classList.contains(`navmgr-hover-menu-user__inner`)) {
          el.style.display = 'block'
        }
        if (el.classList.contains(`navmgr-hover-menu-user__inner__top-menu-items`)) {
          el.style.display = 'block'
        }
      } else if (menuActive && el.classList.contains(`navmgr-hover-menu-${menuActive}`)) {
        el.style.display = 'flex'
      }
    })
  }, [menuActive])

  const getBuildingDashboardFeedFlagsDueToRole = () => {
    // Also unlocked by any of the Pay Rent flags (see OperationsModal's payments <Can>) — the
    // Operations dropdown houses that link, so a Client with payments enabled but no building
    // essentials/perks still needs a way to open the menu and reach it.
    const clientBuildingDashFlags = [
      'buildingEssentialsAvailable',
      'perksAvailable',
      'yardiPaymentId',
      'rentManagerPropertyId',
      'payRentLink',
    ]
    const managersBuildingDashFlags = [
      'managementUsersAvailable',
      'amenityReservations',
      'maintenanceRequestAvailable',
      'guestsAccess',
      'packagesAvailable',
      'groupsAvailable',
      'tvAppAvailable',
    ]

    if (userRole === 'Client') return clientBuildingDashFlags
    if (userRole === 'ShiftManager') return managersBuildingDashFlags
    return []
  }

  return (
    <div
      className={`navmgr ${isReservationsRoute ? 'navmgr--compact' : ''} ${
        isReservationsRoute && burgerOpen ? 'navmgr--burger-open' : ''
      }`}
    >
      <div className="navmgr-left">
        {isReservationsRoute && (
          <button
            type="button"
            className="navmgr-burger"
            aria-label={burgerOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={burgerOpen}
            onClick={(e) => {
              e.stopPropagation()
              setMenuActive(false)
              setBurgerOpen((prev) => !prev)
            }}
          >
            <span />
            <span />
            <span />
          </button>
        )}
        {userRole === 'Client' ? (
          <div className="navmgr-logo">
            <Link to={getLogoLink(userData, role)}>
              <img
                className="chitown-logo"
                src="/images/logo/elevateos-logo-wide-white.svg"
                alt={ELEVATEOS_LOGO_ALT}
              />
            </Link>
          </div>
        ) : (
          <NavbarBuildingSelector />
        )}
        <div className="navmgr-menu">
          <Can on="Navbar" to="buildingDashboardV2" feedFlag="buildingAnalyticsAvailable">
            <div className="navmgr-item">
              <NavLink
                className={({ isActive }) =>
                  isActive ? 'navmgr-item__link--active' : 'navmgr-item__link'
                }
                to="/dashboard/building-analytics"
              >
                <span>
                  {t('navbar:items.dashboard')} <NavigationLabel type={NAVIGATION_LABELS.HIDDEN} />
                </span>
              </NavLink>
            </div>
          </Can>
          <Can on="Navbar" to="calendar" feedFlag="communityCalendar">
            <div className="navmgr-item">
              <NavLink
                to="/dashboard/calendar"
                className={({ isActive }) =>
                  isActive ? 'navmgr-item__link--active' : 'navmgr-item__link'
                }
              >
                <span>
                  {t('navbar:items.calendar')} <NavigationLabel type={NAVIGATION_LABELS.HIDDEN} />
                </span>
              </NavLink>
            </div>
          </Can>
          <Can on="Navbar" to="units">
            <div className="navmgr-item">
              <NavLink
                to="/dashboard/units"
                className={({ isActive }) =>
                  isActive ? 'navmgr-item__link--active' : 'navmgr-item__link'
                }
              >
                <span>
                  {t('navbar:items.units')} <NavigationLabel type={NAVIGATION_LABELS.HIDDEN} />
                </span>
              </NavLink>
            </div>
          </Can>
          <Can on="Navbar" to="clients">
            <div className="navmgr-item">
              <NavLink
                to={clientsPath}
                className={({ isActive }) =>
                  isActive ? 'navmgr-item__link--active' : 'navmgr-item__link'
                }
              >
                <span>
                  {t('navbar:items.contacts')} <NavigationLabel type={NAVIGATION_LABELS.HIDDEN} />
                </span>
              </NavLink>
            </div>
          </Can>
          <Can on="Navbar" to="marketing">
            <div
              onClick={(e) => onDropdownMenu(e, 'marketing')}
              className={`navmgr-item navmgr-selector-item ${
                menuActive === 'marketing' ||
                getParentItemHighlighted(location.pathname, marketingChildrenPathname)
                  ? 'navmgr-item-user-active'
                  : ''
              }`}
            >
              <span>
                {t('navbar:items.marketing')}
                <Icon icon="arrow" fill="white" className="navmgr_item__dropdown-icon" />
                <NavigationLabel type={NAVIGATION_LABELS.HIDDEN} />
              </span>
              <MarketingDropdown />
            </div>
          </Can>
          <Can
            on="Navbar"
            to="buildingDashboard"
            feedFlag={getBuildingDashboardFeedFlagsDueToRole()}
            isAny
          >
            <div
              onClick={(e) => onDropdownMenu(e, 'operations')}
              className={`navmgr-item navmgr-selector-item ${
                menuActive === 'operations' ||
                getParentItemHighlighted(location.pathname, operationsChildrenPathname)
                  ? 'navmgr-item-user-active'
                  : ''
              }`}
            >
              <span>
                {t('navbar:items.operations')}
                <Icon icon="arrow" fill="white" className="navmgr_item__dropdown-icon" />
                <NavigationLabel type={NAVIGATION_LABELS.HIDDEN} />
              </span>
              <OperationsModal userRole={role} />
            </div>
          </Can>
          <Can
            on={'Navbar'}
            to={'chat'}
            feedFlag={getChatFeedFlagsDueToRole(userRole)}
            isAll
            allBuildings
            upsellingLink={<UnifiedInboxLink withUpsellingRedirect />}
          >
            <UnifiedInboxLink />
          </Can>
          <Can on={'Navbar'} to={'integrationsPM'}>
            <div
              onClick={(e) => onDropdownMenu(e, 'explore')}
              className={`navmgr-item navmgr-selector-item ${
                menuActive === 'explore' ||
                getParentItemHighlighted(location.pathname, exploreChildrenPathname)
                  ? 'navmgr-item-user-active'
                  : ''
              }`}
            >
              <span>
                {t('navbar:items.explore')}
                <Icon icon="arrow" fill="white" className="navmgr_item__dropdown-icon" />
                <NavigationLabel type={NAVIGATION_LABELS.NEW} />
              </span>
              <ExploreModal />
            </div>
          </Can>
          <Can on={'Navbar'} to={'organisation'}>
            <div className="navmgr-item">
              <div
                onClick={(e) => onDropdownMenu(e, 'dash')}
                className={`navmgr-item navmgr-selector-item ${
                  menuActive === 'dash' ||
                  getParentItemHighlighted(location.pathname, dashModalChildrenPathname)
                    ? 'navmgr-item-user-active'
                    : ''
                }`}
              >
                <span>
                  {t('navbar:items.adminSettings')}
                  <Icon icon="arrow" fill="white" className="navmgr_item__dropdown-icon" />
                  <NavigationLabel type={NAVIGATION_LABELS.HIDDEN} />
                </span>
                <DashModal />
              </div>
            </div>
          </Can>
          <Can on={'Navbar'} to={'clientReservations'}>
            <div className="navmgr-item">
              <div
                onClick={handleOpenReservations}
                className={`navmgr-item navmgr-selector-item ${
                  menuActive === 'reservations' ||
                  getParentItemHighlighted(location.pathname, reservationsModalChildrenPathname)
                    ? 'navmgr-item-user-active'
                    : ''
                }`}
              >
                <NavLink
                  to="/dashboard/client-reservations"
                  className={({ isActive }) =>
                    isActive ? 'navmgr-item__link--active' : 'navmgr-item__link'
                  }
                >
                  <span>{t('navbar:items.reservations')}</span>
                  <NavigationLabel type={NAVIGATION_LABELS.HIDDEN} />
                </NavLink>
              </div>
            </div>
          </Can>
        </div>
      </div>

      <div className="navmgr-right">
        <Can on="Navbar" to="nPortal" feedFlag="notificationsAvailable">
          <div className="navmgr-bell">
            <Link
              className={({ isActive }) =>
                isActive ? 'navmgr-item__link--active' : 'navmgr-item__link'
              }
              to="/dashboard/nportal"
            >
              <img src="/images/bell.png" alt="bell" />
            </Link>
          </div>
        </Can>
        <div
          onClick={(e) => onDropdownMenu(e, 'user')}
          className={`navmgr-item-user ${
            menuActive === 'user' ||
            getParentItemHighlighted(location.pathname, userMenuModalChildrenPathname)
              ? 'navmgr-item-user-active'
              : ''
          }`}
        >
          <div className="navmgr-item-user__info">
            <Avatar
              className="navmgr-item-user__avatar"
              src={getAssetLink({ path: 'chitown-avatar/avatar', file: avatar })}
              alt={`${firstName} ${lastName}`}
              size="small"
            />
            <UserMenuModal onLogout={onLogout} />
            <span className="navmgr-item-user__name">{`${firstName} ${lastName}`}</span>
            <Icon icon="arrow" fill="white" className="navmgr_item-user__dropdown-icon" />
          </div>
        </div>
      </div>
    </div>
  )
}

export default Navbar
